import { useCallback, useEffect, useRef, useState } from "react";
import { generateReports, type GeneratedFile } from "@tax/report";

interface PreparedFile {
  state: string;
  stateAbbrev: string;
  filename: string;
  sizeBytes: number;
  orderCount: number;
  url: string;
}

const FILING_STATES = new Set([
  "AL", "AZ", "AR", "CO", "DC", "FL", "GA", "IL", "IN", "KY", "LA",
  "MI", "MS", "MO", "NV", "NC", "PA", "SC", "TN", "TX", "VA",
]);

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function toBlobUrl(bytes: Uint8Array): string {
  // Copy into a fresh ArrayBuffer so the Blob owns its bytes independently.
  const ab = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(ab).set(bytes);
  return URL.createObjectURL(new Blob([ab], { type: XLSX_MIME }));
}

export function App() {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Generating reports…");
  const [files, setFiles] = useState<PreparedFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (files) for (const f of files) URL.revokeObjectURL(f.url);
    };
  }, [files]);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }, []);

  const submit = useCallback(async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    if (files) for (const f of files) URL.revokeObjectURL(f.url);
    setFiles(null);

    try {
      setBusyLabel(`Reading ${file.name}…`);
      const buf = new Uint8Array(await file.arrayBuffer());

      setBusyLabel("Processing orders and building state workbooks…");
      // Yield so React can paint the progress label before the CPU-bound run.
      await new Promise((r) => setTimeout(r, 30));

      const generated: GeneratedFile[] = await generateReports(buf);
      if (generated.length === 0) {
        throw new Error("No US-state orders found in the uploaded file.");
      }

      const prepared: PreparedFile[] = generated.map((g) => ({
        state: g.state,
        stateAbbrev: g.stateAbbrev,
        filename: g.filename,
        sizeBytes: g.sizeBytes,
        orderCount: g.orderCount,
        url: toBlobUrl(g.buffer),
      }));
      setFiles(prepared);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [file, files]);

  const reset = useCallback(() => {
    if (files) for (const f of files) URL.revokeObjectURL(f.url);
    setFile(null);
    setFiles(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [files]);

  return (
    <div className="app">
      <header className="header">
        <h1>Shopify Tax Report Generator</h1>
        <p className="subtitle">
          Upload a Shopify sales export and download one styled <code>.xlsx</code> per US state,
          with one tab per month. Everything happens in this browser tab — the file is never uploaded.
        </p>
      </header>

      {!files && (
        <section
          className={`dropzone ${dragOver ? "dropzone--active" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="file-pill">
              <strong>{file.name}</strong>
              <span>{formatBytes(file.size)}</span>
            </div>
          ) : (
            <>
              <div className="dropzone-icon">⬆</div>
              <p>
                <strong>Drop your Shopify export here</strong>, or click to browse
              </p>
              <p className="hint">Accepts .csv, .xls, .xlsx — Shopify sales/tax report or Orders export</p>
            </>
          )}
        </section>
      )}

      {!files && (
        <div className="actions">
          <button
            className="btn btn--primary"
            disabled={!file || busy}
            onClick={submit}
          >
            {busy ? busyLabel : "Generate state reports"}
          </button>
          {file && !busy && (
            <button className="btn btn--ghost" onClick={reset}>
              Clear
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert--error">
          <strong>Something went wrong.</strong> {error}
        </div>
      )}

      {files && (
        <section className="results">
          <div className="results-header">
            <h2>{files.length} state report{files.length === 1 ? "" : "s"} ready</h2>
            <button className="btn btn--ghost" onClick={reset}>
              Process another file
            </button>
          </div>
          <p className="hint">
            Files are held only in this browser tab. Closing or refreshing this page will discard them.
            Highlighted rows are on the standard filing list.
          </p>
          <ul className="file-list">
            {files.map((f) => (
              <li
                key={f.stateAbbrev}
                className={`file-row ${FILING_STATES.has(f.stateAbbrev) ? "file-row--filing" : ""}`}
              >
                <div className="file-meta">
                  <div className="file-state">
                    {f.state}
                    <span className="abbrev">{f.stateAbbrev}</span>
                    {FILING_STATES.has(f.stateAbbrev) && (
                      <span className="badge">Filing state</span>
                    )}
                  </div>
                  <div className="file-sub">
                    {f.orderCount} order{f.orderCount === 1 ? "" : "s"} ·{" "}
                    {formatBytes(f.sizeBytes)}
                  </div>
                </div>
                <a className="btn btn--primary" href={f.url} download={f.filename}>
                  Download
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="footer">
        Internal tool · processed entirely in your browser · no data is uploaded
      </footer>
    </div>
  );
}
